import React, { useEffect, useMemo, useState } from 'react';
import {
  DocumentArrowDownIcon,
  DocumentArrowUpIcon,
  PaperAirplaneIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Company, Deal, InvoiceCustomization, dealsAPI } from '../../services/api';
import { Button, Modal } from '../ui';
import ClientRecipientSelector, { getDefaultClientRecipients } from '../emails/ClientRecipientSelector';

export interface InvoiceGenerationResult {
  action: 'download' | 'email';
  message: string;
  recipients?: string[];
}

interface InvoiceGeneratorModalProps {
  isOpen: boolean;
  onClose: () => void;
  deals: Deal[];
  companies: Company[];
  initialDealId?: number;
  lockDeal?: boolean;
  onSuccess?: (result: InvoiceGenerationResult) => void | Promise<void>;
}

const formatInputDate = (value?: string) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return new Date().toISOString().slice(0, 10);
  return date.toISOString().slice(0, 10);
};

const formatCompanyAddress = (company?: Company) => [
  company?.address,
  [company?.city, company?.state, company?.postalCode].filter(Boolean).join(', '),
  company?.country,
].filter(Boolean).join('\n');

export const buildInvoiceCustomization = (deal?: Deal, company?: Company): InvoiceCustomization => {
  const companyAddress = formatCompanyAddress(company);
  const clientPhone = deal?.client?.phoneNumbers?.[0];
  const clientAddress = [
    deal?.client?.address,
    clientPhone ? `Phone: ${clientPhone}` : '',
  ].filter(Boolean).join('\n');

  return {
    invoiceNumber: deal?.orderNumber || (deal?.id ? String(deal.id) : ''),
    invoiceDate: formatInputDate(deal?.createdAt),
    shipperName: company?.name || '',
    shipperAddress: companyAddress,
    shipperEmail: company?.email || '',
    shipperWebsite: company?.website || '',
    consigneeName: deal?.client?.name || '',
    consigneeAddress: clientAddress,
    consigneeEmail: deal?.client?.email || '',
    advisingBank: [
      company?.name ? `Company name: ${company.name}` : '',
      companyAddress ? `Address: ${companyAddress.replace(/\n/g, ', ')}` : '',
      company?.taxId ? `Tax ID: ${company.taxId}` : '',
      company?.registrationNo ? `Registration No: ${company.registrationNo}` : '',
    ].filter(Boolean).join('\n'),
    terms: '100% in advance.',
    incoterms: 'Ex work. Buyer must provide BOL.',
    buyerNotice: 'Buyers must inspect goods upon arrival. Notice of claim or possible claim must be made within 5 days of receipt of goods. Failure to do so will indicate unqualified acceptance of delivered goods and payment of this invoice will be expected in full in accordance with the terms of the sale.',
    termsAndConditions: 'COCOJOJO LLC Terms and Conditions\n1. Controlling Document. Seller accepts the Buyer order subject to and expressly conditioned upon Buyer acceptance of the terms and conditions set forth herein.\n2. Buyer is responsible for payment of all additional costs unless explicitly stated otherwise.\n3. Shipment. Seller will strive to meet Buyer delivery requests but cannot guarantee delivery dates or processing dates.',
    signedByName: '',
    signedByTitle: 'Sales Representative',
    watermarkText: company?.name || '',
    items: (deal?.orderItems || []).map((item) => {
      const unit = item.unit || item.product?.unit || '';
      const total = Number(item.price || 0) * Number(item.quantity || 0);
      return {
        description: item.name || item.product?.name || item.sku || '',
        weight: `${item.quantity || ''}${unit ? ` ${unit}` : ''}`.trim(),
        unitPrice: unit ? `$${Number(item.price || 0).toFixed(2)}/${unit}` : `$${Number(item.price || 0).toFixed(2)}`,
        total: `$${total.toFixed(2)}`,
      };
    }),
  };
};

const InvoiceGeneratorModal: React.FC<InvoiceGeneratorModalProps> = ({
  isOpen,
  onClose,
  deals,
  companies,
  initialDealId,
  lockDeal = false,
  onSuccess,
}) => {
  const activeCompanies = useMemo(() => companies.filter((company) => company.isActive), [companies]);
  const accessibleDeals = useMemo(
    () => deals.filter((deal) => deal.client || deal.orderNumber || deal.description),
    [deals],
  );
  const [form, setForm] = useState({
    dealId: '',
    companyId: '',
    poName: '',
    poDescription: '',
    poFile: null as File | null,
  });
  const [customization, setCustomization] = useState<InvoiceCustomization>(() => buildInvoiceCustomization());
  const [recipients, setRecipients] = useState<string[]>([]);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    const nextDealId = initialDealId ? String(initialDealId) : '';
    const nextCompanyId = activeCompanies[0]?.id?.toString() || '';
    const selectedDeal = deals.find((deal) => deal.id === Number(nextDealId));
    const selectedCompany = activeCompanies.find((company) => company.id === Number(nextCompanyId));

    setForm({
      dealId: nextDealId,
      companyId: nextCompanyId,
      poName: '',
      poDescription: '',
      poFile: null,
    });
    setCustomization(buildInvoiceCustomization(selectedDeal, selectedCompany));
    setRecipients(getDefaultClientRecipients(selectedDeal?.client));
    setError('');
  }, [activeCompanies, deals, initialDealId, isOpen]);

  const selectedDeal = deals.find((deal) => deal.id === Number(form.dealId));

  const getDefaults = (dealId = form.dealId, companyId = form.companyId) => buildInvoiceCustomization(
    deals.find((deal) => deal.id === Number(dealId)),
    activeCompanies.find((company) => company.id === Number(companyId)),
  );

  const updateField = (key: keyof InvoiceCustomization, value: string) => {
    setCustomization((current) => ({ ...current, [key]: value }));
  };

  const updateItem = (index: number, patch: Partial<NonNullable<InvoiceCustomization['items']>[number]>) => {
    setCustomization((current) => ({
      ...current,
      items: (current.items || []).map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item),
    }));
  };

  const addItem = () => {
    setCustomization((current) => ({
      ...current,
      items: [...(current.items || []), { description: '', weight: '', unitPrice: '', total: '' }],
    }));
  };

  const removeItem = (index: number) => {
    setCustomization((current) => ({
      ...current,
      items: (current.items || []).filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const uploadPoIfNeeded = async () => {
    if (!form.poFile) return;

    await dealsAPI.uploadDocument(Number(form.dealId), form.poFile, {
      documentType: 'PURCHASE_ORDER',
      name: form.poName.trim() || form.poFile.name,
      description: form.poDescription.trim() || 'Customer PO uploaded during invoice generation',
    });
  };

  const complete = async (result: InvoiceGenerationResult) => {
    await onSuccess?.(result);
    onClose();
  };

  const handleDownload = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.dealId || !form.companyId) {
      setError('Select a deal and company for the invoice');
      return;
    }

    try {
      setGenerating(true);
      setError('');
      await uploadPoIfNeeded();
      await dealsAPI.downloadInvoice(Number(form.dealId), Number(form.companyId), customization);
      await complete({ action: 'download', message: 'Invoice downloaded' });
    } catch (invoiceError: any) {
      setError(invoiceError.response?.data?.message || 'Failed to generate invoice');
    } finally {
      setGenerating(false);
    }
  };

  const handleSend = async () => {
    if (!form.dealId || !form.companyId) {
      setError('Select a deal and company for the invoice');
      return;
    }
    if (recipients.length === 0) {
      setError('Select at least one invoice recipient');
      return;
    }

    try {
      setSending(true);
      setError('');
      await uploadPoIfNeeded();
      const result = await dealsAPI.sendInvoice(Number(form.dealId), {
        ...customization,
        companyId: Number(form.companyId),
        to: recipients,
      });
      await complete({
        action: 'email',
        message: `Invoice emailed to ${result.recipients.join(', ')}`,
        recipients: result.recipients,
      });
    } catch (invoiceError: any) {
      setError(invoiceError.response?.data?.message || 'Failed to email invoice');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Generate Invoice"
      subtitle={lockDeal
        ? `Create the full Proforma invoice for ${selectedDeal?.orderNumber || `Deal #${initialDealId}`}.`
        : "Choose the deal and company, then optionally attach the customer's purchase order."}
      size="full"
    >
      <form onSubmit={handleDownload} className="space-y-4">
        {error && (
          <div className="rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-sm font-medium text-error-700">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-neutral-700">Deal *</label>
          <select
            value={form.dealId}
            onChange={(event) => {
              const nextDealId = event.target.value;
              setForm((current) => ({ ...current, dealId: nextDealId }));
              setCustomization(getDefaults(nextDealId, form.companyId));
              setRecipients(getDefaultClientRecipients(
                deals.find((deal) => deal.id === Number(nextDealId))?.client,
              ));
            }}
            required
            disabled={lockDeal}
            className="mt-1 block w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-neutral-100 disabled:text-neutral-700"
          >
            <option value="">Select deal</option>
            {accessibleDeals.map((deal) => (
              <option key={deal.id} value={deal.id}>
                {deal.orderNumber || `Deal #${deal.id}`} - {deal.client?.name || deal.description}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700">Company *</label>
          {activeCompanies.length === 0 ? (
            <div className="mt-1 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2.5 text-sm text-warning-700">
              No active companies are available for invoice generation.
            </div>
          ) : (
            <select
              value={form.companyId}
              onChange={(event) => {
                const nextCompanyId = event.target.value;
                setForm((current) => ({ ...current, companyId: nextCompanyId }));
                setCustomization(getDefaults(form.dealId, nextCompanyId));
              }}
              required
              className="mt-1 block w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {activeCompanies.map((company) => (
                <option key={company.id} value={company.id}>{company.name}</option>
              ))}
            </select>
          )}
        </div>

        <ClientRecipientSelector
          client={selectedDeal?.client}
          value={recipients}
          onChange={setRecipients}
          label="Invoice recipients"
        />

        <div className="rounded-lg border border-neutral-200 bg-white p-4">
          <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h4 className="text-sm font-semibold text-neutral-900">Invoice Fields</h4>
              <p className="mt-1 text-xs text-neutral-500">Adjust the exported Proforma invoice text before download.</p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => setCustomization(getDefaults())}>
              Reset
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <label className="block text-xs font-medium text-neutral-700">Invoice #</label>
              <input
                value={customization.invoiceNumber || ''}
                onChange={(event) => updateField('invoiceNumber', event.target.value)}
                className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-700">Date</label>
              <input
                type="date"
                value={customization.invoiceDate || ''}
                onChange={(event) => updateField('invoiceDate', event.target.value)}
                className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-700">Watermark</label>
              <input
                value={customization.watermarkText || ''}
                onChange={(event) => updateField('watermarkText', event.target.value)}
                className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
            <div className="space-y-3 rounded-lg border border-neutral-200 bg-neutral-50 p-3">
              <h5 className="text-xs font-semibold uppercase text-neutral-500">Shipper & Exporter</h5>
              <input
                aria-label="Shipper Name"
                value={customization.shipperName || ''}
                onChange={(event) => updateField('shipperName', event.target.value)}
                className="block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
              <textarea
                aria-label="Shipper Address"
                value={customization.shipperAddress || ''}
                onChange={(event) => updateField('shipperAddress', event.target.value)}
                rows={3}
                className="block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <input
                  aria-label="Shipper Email"
                  value={customization.shipperEmail || ''}
                  onChange={(event) => updateField('shipperEmail', event.target.value)}
                  placeholder="Email"
                  className="block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
                <input
                  aria-label="Shipper Website"
                  value={customization.shipperWebsite || ''}
                  onChange={(event) => updateField('shipperWebsite', event.target.value)}
                  placeholder="Website"
                  className="block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
            </div>

            <div className="space-y-3 rounded-lg border border-neutral-200 bg-neutral-50 p-3">
              <h5 className="text-xs font-semibold uppercase text-neutral-500">Consignee</h5>
              <input
                aria-label="Consignee Name"
                value={customization.consigneeName || ''}
                onChange={(event) => updateField('consigneeName', event.target.value)}
                className="block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
              <textarea
                aria-label="Consignee Address"
                value={customization.consigneeAddress || ''}
                onChange={(event) => updateField('consigneeAddress', event.target.value)}
                rows={3}
                className="block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
              <input
                aria-label="Consignee Email"
                value={customization.consigneeEmail || ''}
                onChange={(event) => updateField('consigneeEmail', event.target.value)}
                placeholder="Email"
                className="block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-neutral-700">Advising Bank</label>
              <textarea
                value={customization.advisingBank || ''}
                onChange={(event) => updateField('advisingBank', event.target.value)}
                rows={5}
                className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-medium text-neutral-700">Terms</label>
                  <input
                    value={customization.terms || ''}
                    onChange={(event) => updateField('terms', event.target.value)}
                    className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-700">Incoterms</label>
                  <input
                    value={customization.incoterms || ''}
                    onChange={(event) => updateField('incoterms', event.target.value)}
                    className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-medium text-neutral-700">Signed By</label>
                  <input
                    value={customization.signedByName || ''}
                    onChange={(event) => updateField('signedByName', event.target.value)}
                    className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-700">Signer Title</label>
                  <input
                    value={customization.signedByTitle || ''}
                    onChange={(event) => updateField('signedByTitle', event.target.value)}
                    className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <div className="mb-2 flex items-center justify-between">
              <label className="block text-xs font-medium text-neutral-700">Line Items</label>
              <Button type="button" variant="outline" size="sm" onClick={addItem} leftIcon={<PlusIcon className="h-4 w-4" />}>
                Add Item
              </Button>
            </div>
            <div className="space-y-3">
              {(customization.items || []).map((item, index) => (
                <div key={index} className="grid grid-cols-1 gap-2 rounded-lg border border-neutral-200 bg-neutral-50 p-3 lg:grid-cols-12">
                  <input
                    aria-label={`Item ${index + 1} Description`}
                    value={item.description || ''}
                    onChange={(event) => updateItem(index, { description: event.target.value })}
                    placeholder="Description"
                    className="block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500 lg:col-span-5"
                  />
                  <input
                    aria-label={`Item ${index + 1} Weight`}
                    value={item.weight || ''}
                    onChange={(event) => updateItem(index, { weight: event.target.value })}
                    placeholder="Weight"
                    className="block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500 lg:col-span-2"
                  />
                  <input
                    aria-label={`Item ${index + 1} Unit Price`}
                    value={item.unitPrice || ''}
                    onChange={(event) => updateItem(index, { unitPrice: event.target.value })}
                    placeholder="Unit price"
                    className="block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500 lg:col-span-2"
                  />
                  <input
                    aria-label={`Item ${index + 1} Total`}
                    value={item.total || ''}
                    onChange={(event) => updateItem(index, { total: event.target.value })}
                    placeholder="Total"
                    className="block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500 lg:col-span-2"
                  />
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="inline-flex items-center justify-center rounded-md border border-neutral-200 bg-white p-2 text-neutral-500 hover:bg-neutral-100 lg:col-span-1"
                    aria-label={`Remove item ${index + 1}`}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-neutral-700">Buyer Notice</label>
              <textarea
                value={customization.buyerNotice || ''}
                onChange={(event) => updateField('buyerNotice', event.target.value)}
                rows={4}
                className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-700">Terms & Conditions</label>
              <textarea
                value={customization.termsAndConditions || ''}
                onChange={(event) => updateField('termsAndConditions', event.target.value)}
                rows={4}
                className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
          <div className="mb-3 flex items-center gap-2">
            <DocumentArrowUpIcon className="h-5 w-5 text-neutral-500" />
            <h4 className="text-sm font-semibold text-neutral-900">Optional Customer PO</h4>
          </div>
          <div className="space-y-3">
            <input
              value={form.poName}
              onChange={(event) => setForm((current) => ({ ...current, poName: event.target.value }))}
              placeholder="PO file name"
              className="block w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <textarea
              value={form.poDescription}
              onChange={(event) => setForm((current) => ({ ...current, poDescription: event.target.value }))}
              rows={2}
              placeholder="PO description"
              className="block w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <input
              type="file"
              onChange={(event) => setForm((current) => ({ ...current, poFile: event.target.files?.[0] || null }))}
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.xlsx,.xls"
              className="block w-full text-sm text-neutral-500 file:mr-3 file:rounded-md file:border-0 file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-700 hover:file:bg-primary-100"
            />
            {form.poFile && <p className="text-xs text-neutral-500">Selected: {form.poFile.name}</p>}
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 pt-3 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={onClose} disabled={generating || sending}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="secondary"
            isLoading={sending}
            disabled={!form.dealId || !form.companyId || activeCompanies.length === 0 || generating || recipients.length === 0}
            onClick={handleSend}
            leftIcon={<PaperAirplaneIcon className="h-4 w-4" />}
          >
            Email Invoice
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={generating}
            disabled={!form.dealId || !form.companyId || activeCompanies.length === 0 || sending}
            leftIcon={<DocumentArrowDownIcon className="h-4 w-4" />}
          >
            Download Invoice
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default InvoiceGeneratorModal;
